import type { TicketLabelAdmin, TicketPriority, TicketTraceStatus } from '@traceio-org/platform-contracts';
import { PRODUCTS, TICKET_PRIORITIES, TICKET_TRACE_STATUSES } from '@traceio-org/platform-contracts';
import type { TicketStaffQueueQuery } from '@traceio-org/platform-client';
import { MenuSelect } from '../../components/MenuSelect';
import { PRIORITY_LABELS, TRACE_STATUS_LABELS } from './ticketFormat';
import type { TicketDisplay } from './TicketList';

/** The queue's views: Inbox, My tickets and All (spec), plus the staff member's saved ones. */
export const QUEUE_VIEWS = [
  { value: 'inbox', label: 'Inbox' },
  { value: 'mine', label: 'My tickets' },
  { value: 'all', label: 'All' },
] as const;
export type QueueView = (typeof QUEUE_VIEWS)[number]['value'];

/**
 * The queue's state as its address carries it (so a reload, a shared link, back and a saved view keep it): the view,
 * one value per filter, the display and the page cursor.
 */
export interface QueueState {
  view: QueueView;
  status: TicketTraceStatus | '';
  priority: TicketPriority | '';
  label: string;
  /** 'none' (unassigned), 'me', or ''. */
  assignee: '' | 'none' | 'me';
  overdue: boolean;
  product: string;
  /** The customer (orgId), or ''. */
  org: string;
  /** Filed at least this many days ago, or 0 (any age). */
  age: number;
  /** The search: a ticket number (or its start) or words of the title. */
  q: string;
  display: TicketDisplay;
  cursor: string;
}

/** The age filter's choices, in days. */
export const AGE_DAYS = [1, 3, 7, 30] as const;

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | '' =>
  value !== null && (values as readonly string[]).includes(value) ? (value as T) : '';

export function readQueueState(params: URLSearchParams): QueueState {
  return {
    view:
      oneOf(
        QUEUE_VIEWS.map((v) => v.value),
        params.get('view'),
      ) || 'inbox',
    status: oneOf(TICKET_TRACE_STATUSES, params.get('status')),
    priority: oneOf(TICKET_PRIORITIES, params.get('priority')),
    label: params.get('label') ?? '',
    assignee: oneOf(['none', 'me'] as const, params.get('assignee')),
    overdue: params.get('overdue') === 'true',
    product: params.get('product') ?? '',
    org: params.get('org') ?? '',
    age: AGE_DAYS.includes(Number(params.get('age')) as (typeof AGE_DAYS)[number])
      ? Number(params.get('age'))
      : 0,
    q: (params.get('q') ?? '').trim().slice(0, 200),
    display: params.get('display') === 'board' ? 'board' : 'list',
    cursor: params.get('cursor') ?? '',
  };
}

/** The address of a state: only what differs from the defaults, in a stable order (saved views compare it). */
export function queueSearch(state: QueueState, { withCursor = true } = {}): string {
  const params = new URLSearchParams();
  if (state.view !== 'inbox') params.set('view', state.view);
  if (state.status) params.set('status', state.status);
  if (state.priority) params.set('priority', state.priority);
  if (state.label) params.set('label', state.label);
  if (state.assignee) params.set('assignee', state.assignee);
  if (state.overdue) params.set('overdue', 'true');
  if (state.product) params.set('product', state.product);
  if (state.org) params.set('org', state.org);
  if (state.age) params.set('age', String(state.age));
  if (state.q) params.set('q', state.q);
  if (state.display === 'board') params.set('display', 'board');
  if (withCursor && state.cursor) params.set('cursor', state.cursor);
  return params.toString();
}

/** What platform-api is asked for: the view and the filters (the display is the page's own). */
export function queueQuery(state: QueueState, staffUserId: string): TicketStaffQueueQuery {
  return {
    view: state.view,
    ...(state.status ? { status: [state.status] } : {}),
    ...(state.priority ? { priority: [state.priority] } : {}),
    ...(state.label ? { labelIds: [state.label] } : {}),
    ...(state.assignee === 'none' ? { assignee: 'none' } : {}),
    ...(state.assignee === 'me' ? { assignee: staffUserId } : {}),
    ...(state.overdue ? { overdue: true } : {}),
    ...(state.product ? { product: state.product } : {}),
    ...(state.org ? { orgId: state.org } : {}),
    ...(state.age ? { olderThanDays: state.age } : {}),
    ...(state.q ? { q: state.q } : {}),
    ...(state.cursor ? { cursor: state.cursor } : {}),
  };
}

const ANY = '';

/**
 * The filter toolbar: one compact menu per filter (status, priority, label, assignee, response target, app), each
 * named "<filter>: <choice>". A change goes back to the first page.
 */
export function TicketFilters({
  state,
  labels,
  customers,
  onCustomersWanted,
  onChange,
}: {
  state: QueueState;
  labels: readonly TicketLabelAdmin[];
  /** The organizations to choose from (read only once the customer menu is wanted), or null while not read. */
  customers: readonly { id: string; displayName: string }[] | null;
  /** The customer menu opened: read the organizations. */
  onCustomersWanted: () => void;
  onChange: (next: QueueState) => void;
}) {
  const change = (patch: Partial<QueueState>) => onChange({ ...state, ...patch, cursor: '' });
  const customerOptions = [
    { value: ANY, label: 'Any' },
    ...(customers ?? []).map((c) => ({ value: c.id, label: c.displayName })),
  ];
  // An address that names a customer before the list arrived still says one is chosen.
  if (state.org && !customerOptions.some((o) => o.value === state.org)) {
    customerOptions.push({ value: state.org, label: customers ? 'Unknown customer' : 'Loading…' });
  }
  return (
    <div className="admin-ticket-filters" role="group" aria-label="Filters">
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Customer"
        value={state.org}
        options={customerOptions}
        onOpenChange={(open) => {
          if (open) onCustomersWanted();
        }}
        onChange={(org) => change({ org })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Age"
        value={state.age}
        options={[
          { value: 0, label: 'Any' },
          ...AGE_DAYS.map((days) => ({
            value: days,
            label: `Older than ${days === 1 ? '1 day' : `${days} days`}`,
          })),
        ]}
        onChange={(age) => change({ age })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Status"
        value={state.status}
        options={[
          { value: ANY, label: 'Any' },
          ...TICKET_TRACE_STATUSES.map((s) => ({ value: s, label: TRACE_STATUS_LABELS[s] })),
        ]}
        onChange={(status) => change({ status })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Priority"
        value={state.priority}
        options={[
          { value: ANY, label: 'Any' },
          ...TICKET_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] })),
        ]}
        onChange={(priority) => change({ priority })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Label"
        value={state.label}
        options={[
          { value: ANY, label: 'Any' },
          // Archived labels stay on their tickets, so they still filter.
          ...labels.map((l) => ({ value: l.id, label: l.archivedAt ? `${l.name} (archived)` : l.name })),
        ]}
        onChange={(label) => change({ label })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Assignee"
        value={state.assignee}
        options={[
          { value: ANY, label: 'Anyone' },
          { value: 'me', label: 'Me' },
          { value: 'none', label: 'Unassigned' },
        ]}
        onChange={(assignee) => change({ assignee })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="Response target"
        value={state.overdue ? 'overdue' : ANY}
        options={[
          { value: ANY, label: 'Any' },
          { value: 'overdue', label: 'Overdue' },
        ]}
        onChange={(value) => change({ overdue: value === 'overdue' })}
      />
      <MenuSelect
        variant="compact"
        labelInTrigger
        align="start"
        label="App"
        value={state.product}
        options={[
          { value: ANY, label: 'Any' },
          ...Object.entries(PRODUCTS).map(([key, product]) => ({ value: key, label: product.shortName })),
        ]}
        onChange={(product) => change({ product })}
      />
    </div>
  );
}
